import * as React from "react"
import { cn } from "@/lib/utils"

interface SliderProps {
  value: number;
  onValueChange: (value: number) => void;
  min: number;
  max: number;
  step: number;
  className?: string;
}

const Slider = React.forwardRef<HTMLDivElement, SliderProps>(
  ({ value, onValueChange, min, max, step, className }, ref) => {
    const [isDragging, setIsDragging] = React.useState(false);
    const sliderRef = React.useRef<HTMLDivElement>(null);
    const actualRef = ref || sliderRef;
    
    const getPercentage = (val: number) => ((val - min) / (max - min)) * 100;
    
    const updateValue = React.useCallback((clientX: number) => {
      const element = (actualRef as React.RefObject<HTMLDivElement>).current;
      if (!element) return;
      
      const rect = element.getBoundingClientRect();
      const percentage = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
      const newValue = min + (percentage / 100) * (max - min);
      const steppedValue = Math.round(newValue / step) * step;
      
      onValueChange(steppedValue);
    }, [min, max, step, onValueChange, actualRef]);
    
    const handleMouseDown = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
      updateValue(e.clientX);
    };
    
    const handleClick = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      updateValue(e.clientX);
    };
    
    const handleMouseMove = React.useCallback((e: MouseEvent) => {
      if (isDragging) {
        e.preventDefault();
        updateValue(e.clientX);
      }
    }, [isDragging, updateValue]);
    
    const handleMouseUp = React.useCallback(() => {
      setIsDragging(false);
    }, []);
    
    React.useEffect(() => {
      if (isDragging) {
        document.addEventListener('mousemove', handleMouseMove, { passive: false });
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
          document.removeEventListener('mousemove', handleMouseMove);
          document.removeEventListener('mouseup', handleMouseUp);
        };
      }
    }, [isDragging, handleMouseMove, handleMouseUp]);
    
    return (
      <div
        ref={actualRef}
        className={cn(
          "relative flex w-full touch-none select-none items-center h-5 cursor-pointer",
          className
        )}
        onClick={handleClick}
      >
        <div className="relative h-2 w-full grow overflow-hidden rounded-full bg-secondary">
          <div 
            className="absolute h-full bg-slate-400 rounded-full"
            style={{
              width: `${getPercentage(value)}%`
            }}
          />
        </div>
        <div
          className="absolute block h-5 w-5 rounded-full border-2 border-slate-400 bg-background cursor-pointer transition-colors hover:bg-slate-100 z-10"
          style={{ left: `calc(${getPercentage(value)}% - 10px)` }}
          onMouseDown={handleMouseDown}
        />
      </div>
    );
  }
);

Slider.displayName = 'Slider';

export { Slider }
